import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import {Link, useNavigate} from 'react-router-dom';
import {ButtonBase} from "@mui/material";
import {useTheme} from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import {useFormik} from "formik";
import * as yup from "yup";
import MyGoogleIcon from "../../assets/MyGoogleIcon";


const validationSchema = yup.object({
    name: yup.string().required('Name is required'),
    email: yup
        .string()
        .trim()
        .email('Please enter a valid email address')
        .required('Email is required.'),
    password: yup
        .string()
        .required('Please specify your password')
        .min(8, 'The password should have at minimum length of 8'),
});

const V2SignupForm = ({
                          selectedPricingOption,
                          signupWithGoogle,
                          closeSignupOpenLogin,
                          signUpWithEmailAndPassword,
                      }) => {
    const theme = useTheme();
    const { mode } = theme.palette;

    const initialValues = {
        name: '',
        email: '',
        password: '',
    };

    const registerWithGoogle = () => {
        signupWithGoogle({ pricingOption: selectedPricingOption });
    };

    const onSubmit = (values) => {
        signUpWithEmailAndPassword({
            displayName: values.name,
            email: values.email,
            password: values.password,
            pricingOption: selectedPricingOption,
        });
    };

    const formik = useFormik({
        initialValues,
        validationSchema: validationSchema,
        onSubmit,
    });

    const buttonCommonSx = {
        backgroundColor: '#000000',
        color: '#ffffff',
        height: 44,
        borderRadius: '12px',
        textTransform: 'none',
        fontFamily: '"Inter", sans-serif',
        fontWeight: 500,
        fontSize: '15px',
        boxShadow: mode === 'light' ? theme.shadows[1] : 'none',
        '&:hover': {
            backgroundColor: '#111111',
            boxShadow: mode === 'light' ? theme.shadows[2] : 'none',
        },
    };

    return (
        <Box
            sx={{
                width: '100%',
                maxWidth: 350,
                mx: 'auto',
                px: { xs: 1, sm: 0 },
                py: { xs: 2, sm: 0 },
            }}
        >
            {/* Logo Section */}
            <Box sx={{ mb: { xs: 3, md: 4 } }}>
                <ButtonBase>
                    <Box
                        display="flex"
                        component="a"
                        href="/"
                        title="Libretto"
                        width={{ xs: 150, md: 180 }}
                        height="auto"
                        alignItems="center"
                        justifyContent="center"
                    >
                        <Box component="img" src="/v2-logo.svg" height={1} width={1} />
                    </Box>
                </ButtonBase>
            </Box>

            {/* Text Section */}
            <Box sx={{ mb: theme.spacing(3) }}>
                <Typography
                    sx={{
                        fontFamily: '"Hedvig Letters Serif", serif',
                        fontSize: { xs: '26px', sm: '32px' },
                        lineHeight: { xs: '32px', sm: '38px' },
                        letterSpacing: '-1.3px',
                        fontWeight: 600,
                        mb: theme.spacing(1),
                        color: 'black',
                    }}
                >
                    Create account
                </Typography>

                <Typography
                    sx={{
                        fontFamily: '"Inter", sans-serif',
                        fontSize: { xs: '12px', sm: '13px' },
                        lineHeight: '20px',
                        opacity: 0.8,
                        color: 'black',
                    }}
                >
                    Already have one?{' '}
                    <ButtonBase
                        onClick={closeSignupOpenLogin}
                        sx={{
                            ml: 0.5,
                            textDecoration: 'none',
                            paddingBottom: 0.25,
                        }}
                    >
                        <Typography
                            component="span"
                            sx={{
                                fontFamily: '"Inter", sans-serif',
                                fontSize: { xs: '12px', sm: '13px' },
                                lineHeight: '20px',
                                fontWeight: 600,
                                color: 'black',
                                textDecoration: 'underline',
                                textUnderlineOffset: '2px',
                            }}
                        >
                            Log in
                        </Typography>
                    </ButtonBase>
                </Typography>

                {/* Waitlist explanation */}
                <Typography
                    sx={{
                        mt: 0.75,
                        fontFamily: '"Inter", sans-serif',
                        fontSize: { xs: '11px', sm: '12px' },
                        lineHeight: '18px',
                        opacity: 0.7,
                        color: 'black',
                    }}
                >
                    Libretto is in limited release. Creating an account adds you to the waitlist.
                </Typography>

                {/* Google Sign-Up Button */}
                <Button
                    onClick={registerWithGoogle}
                    startIcon={<MyGoogleIcon />}
                    variant="contained"
                    fullWidth
                    sx={{
                        ...buttonCommonSx,
                        mt: 3,
                    }}
                >
                    Continue with Google
                </Button>

                <form onSubmit={formik.handleSubmit}>
                    <Box display="flex" alignItems="center" my={theme.spacing(3)}>
                        <Box flex="1" height="1px" bgcolor="divider" />
                        <Typography
                            sx={{
                                mx: theme.spacing(2),
                                fontFamily: '"Inter", sans-serif',
                                fontSize: { xs: '11px', sm: '12px' },
                                letterSpacing: '0.1em',
                                textTransform: 'uppercase',
                                opacity: 0.7,
                            }}
                        >
                            Or
                        </Typography>
                        <Box flex="1" height="1px" bgcolor="divider" />
                    </Box>

                    {/* Name */}
                    <Typography
                        sx={{
                            fontFamily: '"Inter", sans-serif',
                            fontWeight: 600,
                            fontSize: { xs: '12px', sm: '13px' },
                            mb: 0.5,
                        }}
                    >
                        Name
                    </Typography>
                    <TextField
                        variant="outlined"
                        name="name"
                        fullWidth
                        value={formik.values.name}
                        onChange={formik.handleChange}
                        error={formik.touched.name && Boolean(formik.errors.name)}
                        // helperText={formik.touched.name && formik.errors.name}
                        sx={{
                            mb: 2,
                            '& .MuiInputBase-root': {
                                height: 44,
                                fontFamily: '"Inter", sans-serif',
                                fontSize: { xs: '13px', sm: '14px' },
                            },
                        }}
                    />

                    {/* Email */}
                    <Typography
                        sx={{
                            fontFamily: '"Inter", sans-serif',
                            fontWeight: 600,
                            fontSize: { xs: '12px', sm: '13px' },
                            mb: 0.5,
                        }}
                    >
                        Email
                    </Typography>
                    <TextField
                        variant="outlined"
                        name="email"
                        fullWidth
                        value={formik.values.email}
                        onChange={formik.handleChange}
                        error={formik.touched.email && Boolean(formik.errors.email)}
                        // helperText={formik.touched.email && formik.errors.email}
                        sx={{
                            mb: 2,
                            '& .MuiInputBase-root': {
                                height: 44,
                                fontFamily: '"Inter", sans-serif',
                                fontSize: { xs: '13px', sm: '14px' },
                            },
                        }}
                    />

                    {/* Password */}
                    <Typography
                        sx={{
                            fontFamily: '"Inter", sans-serif',
                            fontWeight: 600,
                            fontSize: { xs: '12px', sm: '13px' },
                            mb: 0.5,
                        }}
                    >
                        Password
                    </Typography>
                    <TextField
                        variant="outlined"
                        name="password"
                        type="password"
                        fullWidth
                        value={formik.values.password}
                        onChange={formik.handleChange}
                        error={formik.touched.password && Boolean(formik.errors.password)}
                        // helperText={formik.touched.password && formik.errors.password}
                        sx={{
                            mb: 2,
                            '& .MuiInputBase-root': {
                                height: 44,
                                fontFamily: '"Inter", sans-serif',
                                fontSize: { xs: '13px', sm: '14px' },
                            },
                        }}
                    />

                    <Button
                        variant="contained"
                        type="submit"
                        fullWidth
                        sx={{
                            ...buttonCommonSx,
                            mt: 1,
                        }}
                    >
                        Create account
                    </Button>
                </form>
            </Box>

            <Box>{/* extra links / terms if you ever need them */}</Box>
        </Box>
    );
};

export default V2SignupForm;
